import {pickDefined} from '@benchling/js-utils';

import {Permissions} from 'auth/PermissionConstants';
import {
  AnalyticsQuerySpecRequest,
  AnalyticsQuerySpecResponse,
} from 'insights/analytics-queries/RESTAnalyticsQueryTypes';
import {RESTAnalyticsDashboardLayoutRow} from 'insights/layouts/RESTLayoutTypes';
import {
  CONFIG_TYPENAME_TO_PARAMETER_TYPE,
  PARAMETER_TYPE_TO_CONFIG_TYPENAME,
} from 'insights/parameters/ParameterConstants';
import {ParameterType} from 'insights/parameters/ParameterTypes';
import {
  AnalyticsParameterConfigSpecRequest,
  RESTAnalyticsBioentityLinkParameterConfig,
  RESTAnalyticsParameterValue,
  RESTAnalyticsProcessLinkParameterConfig,
  RESTAnalyticsProcessTaskLinkParameterConfig,
  RESTAnalyticsSelectorParameterConfig,
  RESTAnalyticsStorableLinkParameterConfig,
  RESTAnalyticsStudyLinkParameterConfig,
} from 'insights/parameters/RESTParameterTypes';
import {MakeFieldsOptional} from 'util/Types';

import {AnalyticsParameterConfigGraphQL} from '../../../graphql-fragments/insights/__generated/AnalyticsParameterConfigGraphQL';

export interface AnalyticsDashboardSpecResponse {
  id: string;
  analyticsQueries: Array<AnalyticsQuerySpecResponse>;
  description: string;
  name: string;
  layoutRows: Array<RESTAnalyticsDashboardLayoutRow>;
  modifiedAt: string;
  parameterConfigsGQL: Array<AnalyticsParameterConfigGraphQL>;
  parameterValues: Array<RESTAnalyticsParameterValue>;
  permissions: Permissions;
  projectId: string;
  url: string;
}
export type AnalyticsDashboardSpecRequest = MakeFieldsOptional<
  Omit<
    AnalyticsDashboardSpecResponse,
    'analyticsQueries' | 'modifiedAt' | 'parameterConfigsGQL' | 'permissions' | 'url'
  > & {
    analyticsQueries: Array<AnalyticsQuerySpecRequest>;
    parameterConfigs: Array<AnalyticsParameterConfigSpecRequest>;
  },
  'id'
>;

export type UpdateAnalyticsDashboardSpecRequest = AnalyticsDashboardSpecRequest & {
  clientModifiedAt: string;
};

export function getRestTypeForGraphQLParameterConfig(config: AnalyticsParameterConfigGraphQL): ParameterType {
  return CONFIG_TYPENAME_TO_PARAMETER_TYPE[config.__typename];
}

export function getParameterConfigSpecFromReadShape(
  config: AnalyticsParameterConfigGraphQL
): AnalyticsParameterConfigSpecRequest {
  const baseSpec: AnalyticsParameterConfigSpecRequest = {
    ...pickDefined(config, 'id', 'isMulti', 'systemName'),
    type: getRestTypeForGraphQLParameterConfig(config),
  };
  if (config.__typename === 'AnalyticsParameterBioentityLinkConfigNode') {
    return {
      ...baseSpec,
      tagSchemaId: config.tagSchema?.id,
    } as RESTAnalyticsBioentityLinkParameterConfig;
  }
  if (config.__typename === 'AnalyticsParameterProcessTaskLinkConfigNode') {
    return {
      ...baseSpec,
      processTaskSchemaId: config.processTaskSchema?.id,
    } as RESTAnalyticsProcessTaskLinkParameterConfig;
  }
  if (config.__typename === 'AnalyticsParameterProcessLinkConfigNode') {
    return {
      ...baseSpec,
      processTaskSchemaId: config.processTaskSchema?.id,
    } as RESTAnalyticsProcessLinkParameterConfig;
  }
  if (config.__typename === 'AnalyticsParameterSelectorConfigNode') {
    return {
      ...baseSpec,
      schemaFieldSelectorId: config.schemaFieldSelector?.id,
    } as RESTAnalyticsSelectorParameterConfig;
  }
  if (config.__typename === 'AnalyticsParameterStorableLinkConfigNode') {
    return {
      ...baseSpec,
      storableSchemaId: config.storableSchema?.id,
    } as RESTAnalyticsStorableLinkParameterConfig;
  }
  if (config.__typename === 'AnalyticsParameterStudyLinkConfigNode') {
    return {
      ...baseSpec,
      studySchemaId: config.studySchema?.id,
    } as RESTAnalyticsStudyLinkParameterConfig;
  }

  return baseSpec;
}

export function getParameterConfigsSpecFromReadShape(
  configs: Array<AnalyticsParameterConfigGraphQL>
): Array<AnalyticsParameterConfigSpecRequest> {
  return configs.map((config) => getParameterConfigSpecFromReadShape(config));
}

function schemaReference<TypeName extends string>(
  typename: TypeName,
  id: string | null | undefined
): {__typename: TypeName; id: string} | null {
  return id == null ? null : {__typename: typename, id};
}

export function getParameterConfigReadShapeFromConfigSpec(
  config: AnalyticsParameterConfigSpecRequest
): AnalyticsParameterConfigGraphQL {
  const baseConfig = {
    __typename: PARAMETER_TYPE_TO_CONFIG_TYPENAME[config.type],
    ...pickDefined(config, 'id', 'isMulti', 'systemName'),
  };
  switch (config.type) {
    case ParameterType.BIOENTITY_LINK:
      return {
        ...baseConfig,
        tagSchema: schemaReference(
          'TagSchemaNode',
          (config as RESTAnalyticsBioentityLinkParameterConfig).tagSchemaId
        ),
      } as AnalyticsParameterConfigGraphQL;
    case ParameterType.PROCESS_LINK:
      return {
        ...baseConfig,
        processTaskSchema: schemaReference(
          'ProcessTaskSchemaNode',
          (config as RESTAnalyticsProcessLinkParameterConfig).processTaskSchemaId
        ),
      } as AnalyticsParameterConfigGraphQL;
    case ParameterType.PROCESS_TASK_LINK:
      return {
        ...baseConfig,
        processTaskSchema: schemaReference(
          'ProcessTaskSchemaNode',
          (config as RESTAnalyticsProcessTaskLinkParameterConfig).processTaskSchemaId
        ),
      } as AnalyticsParameterConfigGraphQL;
    case ParameterType.SELECTOR:
      return {
        ...baseConfig,
        schemaFieldSelector: schemaReference(
          'SchemaFieldSelectorNode',
          (config as RESTAnalyticsSelectorParameterConfig).schemaFieldSelectorId
        ),
      } as AnalyticsParameterConfigGraphQL;
    case ParameterType.STORABLE_LINK:
      // The REST spec only stores the storable schema id; the concrete storable schema subtype
      // (box/container/location/plate) is not persisted. Consumers only read `.id`, so a
      // representative union __typename is sufficient here.
      return {
        ...baseConfig,
        storableSchema: schemaReference(
          'ContainerSchemaNode',
          (config as RESTAnalyticsStorableLinkParameterConfig).storableSchemaId
        ),
      } as AnalyticsParameterConfigGraphQL;
    case ParameterType.STUDY_LINK:
      return {
        ...baseConfig,
        studySchema: schemaReference(
          'StudySchemaNode',
          (config as RESTAnalyticsStudyLinkParameterConfig).studySchemaId
        ),
      } as AnalyticsParameterConfigGraphQL;
    default:
      return baseConfig as AnalyticsParameterConfigGraphQL;
  }
}

export function getParameterConfigsReadShapeFromConfigSpec(
  configs: Array<AnalyticsParameterConfigSpecRequest>
): Array<AnalyticsParameterConfigGraphQL> {
  return configs.map((config) => getParameterConfigReadShapeFromConfigSpec(config));
}
